import castArray from 'lodash/castArray.js';
import omit from 'lodash/omit.js';
import keyBy from 'lodash/keyBy.js';
import groupBy from 'lodash/groupBy.js';
import orderBy from 'lodash/orderBy.js';
import map from 'lodash/map.js';
import at from 'lodash/at.js';
import chunk from 'lodash/chunk.js';
import clamp from 'lodash/clamp.js';
import cloneDeep from 'lodash/cloneDeep.js';
import isNil from 'lodash/isNil.js';
import {
    applySectionsStyleIcon,
    bulkRemoveSectionOrQuestions,
    changePageActivityQuestion,
    changePageActivityQuestionAnswer,
    copyPageActivityQuestion, createRouteCondition, createTestSection, deleteRouteCondition, deleteTestSection,
    getActivityQuestions, getQuestionRoutes, getTestSections, moveQuestionsToSection,
    removePageActivityQuestion,
    restorePageActivityQuestion,
    sortPageActivityQuestion,
    sortPageActivityQuestionAnswer, sortTestSections, updateQuestionRoute, updateRouteCondition, updateTestSection,
} from '@/api/activityQuestionApi.js';
import { getActivityQuestionRoutes, removeActivityQuestionRoute, sortActivityQuestionRoutes, updateActivityQuestionRoute, createActivityQuestionRoute } from '@/api/activityQuestionRoutesApi.js';
import { createCancelHttp, isHttpCancel } from '@/utils/http.js';
import useSnackbar from '@/hooks/snackbars.js';
import { delay, replaceItemInArrayById } from '@/utils/utils.js';
import { getNewQuestionRouteConditions, getSection, isScoredQuestion, isShouldAnswerQuestion } from '@/components/Blocks/BlockTestOrQuiz/helpers.js';
import {
    QUESTION_ROUTE_CONDITIONS_TYPES,
    QUESTION_ROUTE_DESTINATION_TYPES,
    QUESTION_TYPES,
} from '@/components/Blocks/BlockTestOrQuiz/constants.js';
import { QUESTION_TYPES_WITH_DISABLED_CONDITION_AND } from '@editor-settings/views/TestOrQuizSettings/contants.js';
import { SCORE_TYPE } from '@/constants/index.js';

const { createSnackbar } = useSnackbar();

const CHUNK_SIZE_QUESTIONS = 10;

/**
 * @type {import('vuex').Module<any, any>}
 */
const module = {
    namespaced: true,
    state: {
        blockQuestions: {},
        blockSections: {},
        selectedBlockSection: {},
        selectedQuestionRoutes: [],
        questionRoutes: {},
        isCreatingCondition: false,
        isCreatingRoute: false,
        fullRoutesLoadingProgress: 0,
        newRouteData: null,
        tmpRouteData: null,
        newConditionData: null,
    },
    getters: {
        getActivityQuestionsData: state => blockId => state.blockQuestions[blockId] ?? {},
        getQuestions: (state, getters) => blockId => orderBy(getters.getActivityQuestionsData(blockId)?.questions ?? [], ['order']),
        getOnlyQuestions: (state, getters) => blockId => getters.getQuestions(blockId).filter(q => q.type !== QUESTION_TYPES.INFORMATION),
        getQuestion: (state, getters) => (blockId, questionId) => getters.getQuestions(blockId).find(q => String(q.id) === String(questionId)),
        getQuestionNumber: (state, getters) => (blockId, questionId) => {
            const qIdStr = String(questionId);
            const qIndex = getters
                .getOnlyQuestions(blockId)
                .findIndex(q => String(q.id) === qIdStr);

            return qIndex + 1;
        },
        getActivitySectionsData: state => blockId => state.blockSections[blockId] ?? {},
        getActivitySections: (state, getters) => blockId => orderBy(getters.getActivitySectionsData(blockId)?.sections ?? [], ['order']),
        getSection: (state, getters) => (blockId, sectionId) => {
            const sections = getters.getActivitySections(blockId);

            return sections.find(section => section.id === sectionId);
        },
        getQuestionsGroupedBySections: (state, getters) => blockId => {
            const blockQuestions = getters.getQuestions(blockId);

            return groupBy(blockQuestions, 'test_section_id');
        },
        getSectionQuestions: (state, getters) => (blockId, sectionId) => {
            const questionsBySection = getters.getQuestionsGroupedBySections(blockId);

            return orderBy(questionsBySection[sectionId] ?? [], ['test_section_order']);
        },
        getCurrentSection: (state, getters) => blockId => {
            const sections = getters.getActivitySections(blockId);
            const defaultSection = sections[0];
            const selectedSectionId = state.selectedBlockSection[blockId];

            return getters.getSection(blockId, selectedSectionId) ?? defaultSection;
        },
        getActiveSectionsForActivity: (state, getters) => activityId => {
            const sections = getters.getActivitySections(activityId);

            return sections.filter(s => !s.is_archived);
        },
        getCurrentSectionQuestions: (state, getters) => blockId => {
            const currentSection = getters.getCurrentSection(blockId);

            if (!currentSection) return [];

            return getters.getSectionQuestions(blockId, currentSection.id);
        },
        getQuestionsShouldAnswered: (state, getters) => ({ activityId, withSections }) => {
            if (withSections) {
                const activeSections = getters.getActiveSectionsForActivity(activityId);

                return activeSections.flatMap(s => getters.getSectionQuestionsShouldAnswered({
                    activityId,
                    sectionId: s.id,
                }));
            }

            return getters.getQuestions(activityId).filter(isShouldAnswerQuestion);
        },
        getIsHasInformationQuestion: (state, getters) => ({ activityId, sectionId }) => {
            const questions = sectionId
                ? getters.getSectionQuestions(activityId, sectionId)
                : getters.getQuestions(activityId);

            return questions.some(q => q.type === QUESTION_TYPES.INFORMATION && !q.is_archived);
        },
        getIsHasUnscoredQuestion: (state, getters) => ({ activityId, sectionId }) => {
            const questions = sectionId
                ? getters.getSectionQuestions(activityId, sectionId)
                : getters.getQuestions(activityId);

            return questions.some(q => !q.affects_result);
        },
        getSectionQuestionsShouldAnswered: (state, getters) => ({ activityId, sectionId }) => {
            const sectionQuestions = getters.getSectionQuestions(activityId, sectionId);

            return sectionQuestions.filter(isShouldAnswerQuestion);
        },
        getSectionQuestionsShouldAnsweredCount: (state, getters) => ({ activityId, sectionId }) => getters.getSectionQuestionsShouldAnswered({
            activityId,
            sectionId,
        })?.length || 1,
        getMaxCountQuestionsShouldAnsweredForSections: (state, getters) => activityId => {
            const activeSections = getters.getActiveSectionsForActivity(activityId);

            return activeSections.reduce((acc, s) => acc + Number(s.display_questions_count || getters.getSectionQuestionsShouldAnsweredCount({
                activityId,
                sectionId: s.id,
            })), 0) || 1;
        },
        getMaxCountQuestionShouldAnswered: (state, getters) => ({ activityId, withSections }) => {
            if (withSections) return getters.getMaxCountQuestionsShouldAnsweredForSections(activityId);

            return getters.getQuestionsShouldAnswered({ activityId })?.length || 1;
        },
        getSectionQuestionsAffectResult: (state, getters) => ({ activityId, sectionId }) => {
            const sectionQuestions = getters.getSectionQuestions(activityId, sectionId);

            return sectionQuestions.filter(isScoredQuestion);
        },
        getSectionQuestionsAffectResultCount: (state, getters) => ({ activityId, sectionId }) => getters.getSectionQuestionsAffectResult({
            activityId,
            sectionId,
        })?.length || 1,

        getMaxCountQuestionsAffectResultForSections: (state, getters) => activityId => {
            const activeSections = getters.getActiveSectionsForActivity(activityId);

            return (
                activeSections.reduce(
                    (acc, s) => acc +
                  Number(s.display_questions_count ||
                    getters.getSectionQuestionsAffectResultCount({
                        activityId,
                        sectionId: s.id,
                    })),
                    0,
                ) || 1
            );
        },

        getMaxCountQuestionAffectResult: (state, getters) => ({ activityId, withSections }) => {
            if (withSections) {
                return getters.getMaxCountQuestionsAffectResultForSections(activityId);
            }

            return getters
                .getQuestions(activityId)
                .filter(isScoredQuestion)
                .length;
        },
        getSelectedQuestionRoutes: state => orderBy(state.selectedQuestionRoutes ?? [], ['order']),
        getQuestionRoutesData: state => questionId => state.questionRoutes[questionId],
        getQuestionRoute: (state, getters) => (questionId, routeId) => getters.getQuestionRoutesData(questionId)
            ?.routes
            ?.find(route => String(route.id) === String(routeId)),
        getQuestionRouteConditions: (state, getters) => (questionId, routeId) => getters.getQuestionRoute(questionId, routeId)
            ?.conditions ?? [],
        getQuestionRouteCondition: (state, getters) => (questionId, routeId, conditionId) => getters.getQuestionRouteConditions(questionId, routeId).find(item => String(item.id) === String(conditionId)),
        getConflictingRouteNamesForAnswer: state => (questionId, conditionId, answerId) => {
            const questionRoutesData = state.questionRoutes[questionId];

            if (!questionRoutesData?.routes) return [];

            const conflictingRoutes = questionRoutesData.routes
                .filter(route => route.conditions &&
                    route.conditions.some(condition => condition.id !== conditionId &&
                        condition.answer_id &&
                        String(condition.answer_id) === String(answerId)));

            return conflictingRoutes.map(route => route.name);
        },
    },
    mutations: {
        /**
         * @param state
         * @param {{
         *     activityId: number|string,
         *     loading: boolean,
         *     type: 'sections'|'questions',
         *     data: any
         *  }} payload
         */
        changeData: (state, payload) => {
            const dataFieldName = payload.type === 'questions' ? 'blockQuestions' : 'blockSections';
            const dataField = state[dataFieldName];

            state[dataFieldName] = {
                ...dataField,
                [payload.activityId]: {
                    ...dataField[payload.activityId],
                    ...payload.data,
                },
            };
        },

        /**
         * @param state
         * @param {object} payload
         * @param {*} payload.activityId
         * @param {*} payload.questions
         */
        setActivityQuestions: (state, payload) => {
            state.blockQuestions = {
                ...state.blockQuestions,
                [payload.activityId]: {
                    ...state.blockQuestions[payload.activityId],
                    questions: payload.questions ?? [],
                },
            };
        },

        /**
         * @param state
         * @param payload
         * @param payload.activityId
         * @param payload.questionData
         */
        pushActivityQuestion: (state, payload) => {
            state.blockQuestions = {
                ...state.blockQuestions,
                [payload.activityId]: {
                    ...state.blockQuestions[payload.activityId],
                    questions: [...(state.blockQuestions[payload.activityId]?.questions ?? []), payload.questionData],
                },
            };
        },
        /**
         * @param {*} state
         * @param {{ activityId, questionId, questionData }} payload
         */
        replaceActivityQuestion: (state, payload) => {
            const { activityId, questionId, questionData } = payload;
            const activityQuestionsData = state.blockQuestions[activityId];

            state.blockQuestions[activityId] = {
                ...activityQuestionsData,
                questions: activityQuestionsData?.questions?.map?.(item => (
                    item.id === questionId
                        ? {
                            ...item,
                            ...questionData,
                        }
                        : item
                )) ?? [questionData],
            };
        },

        /**
         * @param state
         * @param {{ activityId, afterQuestionId, questionData }} payload
         */
        insertActivityQuestionAfter: (state, payload) => {
            const { activityId, questionData, afterQuestionId } = payload;

            const activityQuestionsData = state.blockQuestions[activityId];

            const afterQuestionIndex = activityQuestionsData.questions.findIndex(q => q.id === afterQuestionId);

            state.blockQuestions[activityId] = {
                ...activityQuestionsData,
                questions: activityQuestionsData.questions.toSpliced(afterQuestionIndex + 1, 0, questionData),
            };
        },

        /**
         * @param state
         * @param {{ activityId, questionId }} payload
         */
        removeActivityQuestion: (state, payload) => {
            const { activityId, questionId } = payload;

            const activityQuestionsData = state.blockQuestions[activityId];

            state.blockQuestions[activityId] = {
                ...activityQuestionsData,
                questions: activityQuestionsData.questions.filter(q => q.id !== questionId),
            };
        },

        /**
         * @param state
         * @param {{activityId, questionData }} payload
         */
        insertRestoredQuestion(state, payload) {
            const { activityId, questionData } = payload;

            const activityQuestionsData = state.blockQuestions[activityId];

            const newQuestions = [...activityQuestionsData.questions, questionData];

            newQuestions.sort((a, b) => a.order - b.order);

            state.blockQuestions[activityId] = {
                ...activityQuestionsData,
                questions: newQuestions,
            };
        },

        /**
         * @param state
         * @param {{ activityId, questionId, parentId, newAnswer }} payload
         * @returns {Promise<void>}
         */
        updateOrInsertAnswer(state, { activityId, questionId, parentId, newAnswer }) {
            const activityQuestionsData = state.blockQuestions[activityId];

            const newQuestions = activityQuestionsData.questions.map(item => {
                if (item.id === questionId) {
                    const answers = parentId
                        ? item.answers.map(answer => {
                            if (answer.id === parentId) {
                                return {
                                    ...answer,
                                    children: [
                                        ...(answer.children ?? []),
                                        ...castArray(newAnswer),
                                    ],
                                };
                            }

                            return answer;
                        })
                        : [
                            ...item.answers,
                            newAnswer,
                        ];

                    return {
                        ...item,
                        answers,
                    };

                }

                return item;
            });

            state.blockQuestions[activityId] = {
                ...state.blockQuestions[activityId],
                questions: newQuestions,
            };
        },

        /**
         * @param state
         * @param {{ activityId, sectionId }} payload
         */
        setSelectedSection: (state, payload) => {
            state.selectedBlockSection[payload.activityId] = payload.sectionId;
        },

        /**
         * @param state
         * @param {{ activityId }} payload
         */
        resetActivitySections: (state, payload) => {
            const { activityId } = payload;

            state.selectedBlockSection[activityId] = null;
            state.blockSections[activityId] = null;
        },
        /**
         * @param state
         * @param {Array} payload
         */
        setSelectedQuestionRoutes(state, value) {
            state.selectedQuestionRoutes = value;
        },

        /**
         * @param state
         * @param {{ questionId, routes }} payload
         */
        setQuestionRoutes: (state, { questionId, routes }) => {
            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...state.questionRoutes[questionId],
                    routes,
                },
            };
        },

        /**
         * @param state
         * @param {{ questionId, loading }} payload
         */
        setQuestionRoutesLoading: (state, { questionId, loading }) => {
            const existingData = state.questionRoutes[questionId] || {};

            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...existingData,
                    loading,
                },
            };
        },

        /**
         * @param state
         * @param {{ questionId, route }} payload
         */
        changeQuestionRoute: (state, { questionId, route }) => {
            const questionData = state.questionRoutes[questionId];

            if (!questionData?.routes) return;

            const updatedQuestionRoutes = questionData.routes.map(r => (r.id === route.id ? route : r));

            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...questionData,
                    routes: updatedQuestionRoutes,
                },
            };

            state.selectedQuestionRoutes = updatedQuestionRoutes;
        },

        /**
         * @param state
         * @param {{ questionId, routeId, condition }} payload
         */
        changeRouteConditions: (state, { questionId, routeId, conditions }) => {
            const questionData = state.questionRoutes[questionId];

            if (!questionData?.routes) return;

            const updatedQuestionRoutes = questionData.routes.map(route => {
                if (String(route.id) === String(routeId)) {
                    return {
                        ...route,
                        conditions: (route.conditions || [])
                            .map(cond => {
                                const updatedCondition = conditions.find(condition => String(condition.id) === String(cond.id));

                                return updatedCondition || cond;
                            }),
                    };
                }

                return route;
            });

            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...questionData,
                    routes: updatedQuestionRoutes,
                },
            };

            state.selectedQuestionRoutes = updatedQuestionRoutes;
        },

        /**
         * @param state
         * @param {{ questionId, routeId, condition }} payload
         */
        addRouteCondition: (state, { questionId, routeId, condition }) => {
            const questionData = state.questionRoutes[questionId];

            if (!questionData?.routes) return;

            const updatedQuestionRoutes = questionData.routes.map(route => {
                if (String(route.id) === String(routeId)) {
                    return {
                        ...route,
                        conditions: [...(route.conditions || []), condition],
                    };
                }

                return route;
            });

            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...questionData,
                    routes: updatedQuestionRoutes,
                },
            };

            state.selectedQuestionRoutes = updatedQuestionRoutes;
        },

        /**
         * @param state
         * @param {{ questionId, routeId, conditionId }} payload
         */
        deleteRouteCondition: (state, { questionId, routeId, conditionId }) => {
            const questionData = state.questionRoutes[questionId];

            if (!questionData?.routes) return;

            const updatedQuestionRoutes = questionData.routes.map(route => {
                if (String(route.id) === String(routeId)) {
                    return {
                        ...route,
                        conditions: (route.conditions || []).filter(cond => String(cond.id) !== String(conditionId)),
                    };
                }

                return route;
            });

            state.questionRoutes = {
                ...state.questionRoutes,
                [questionId]: {
                    ...questionData,
                    routes: updatedQuestionRoutes,
                },
            };

            state.selectedQuestionRoutes = updatedQuestionRoutes;
        },

        /**
         * @param state
         * @param {boolean} payload
         */
        setIsCreatingCondition(state, payload) {
            state.isCreatingCondition = payload;
        },

        /**
         * @param state
         * @param {boolean} payload
         */
        setIsCreatingRoute(state, payload) {
            state.isCreatingRoute = payload;
        },

        setRoutesLoadingProgress(state, payload) {
            state.fullRoutesLoadingProgress = clamp(payload ?? 0, 0, 100);
        },

        setNewRouteData(state, payload) {
            state.newRouteData = payload;
        },
        setTmpRouteData(state, payload) {
            state.tmpRouteData = payload;
        },
        setNewConditionData(state, payload) {
            state.newConditionData = payload;
        },
        clearNewRouteData(state) {
            state.newRouteData = null;
        },
        clearTmpRouteData(state) {
            state.tmpRouteData = null;
        },
        clearNewConditionData(state) {
            state.newConditionData = null;
        },
    },
    actions: {
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {boolean} payload.loading
         * @param {string|number} payload.activityId
         */
        setLoadingQuestions: ({ commit }, payload) => {
            const { activityId, loading } = payload;

            commit('changeData', {
                activityId,
                type: 'questions',
                data: { loading },
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {*} payload.error
         * @param {string|number} payload.activityId
         */
        setErrorQuestions: ({ commit }, payload) => {
            const { activityId, error } = payload;

            commit('changeData', {
                activityId,
                type: 'questions',
                data: { error },
            });
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {boolean} payload.loading
         * @param {string|number} payload.activityId
         */
        setLoadingSections: ({ commit }, payload) => {
            const { activityId, loading } = payload;

            commit('changeData', {
                activityId,
                type: 'sections',
                data: { loading },
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {*} payload.error
         * @param {string|number} payload.activityId
         */
        setErrorSections: ({ commit }, payload) => {
            const { activityId, error } = payload;

            commit('changeData', {
                activityId,
                type: 'sections',
                data: { error },
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {string|number} payload.activityId
         * @param {*} payload.data
         */
        setSectionsList: ({ commit }, payload) => {
            const { activityId, data } = payload;

            commit('changeData', {
                activityId,
                type: 'sections',
                data: { sections: data },
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {object} payload
         * @param {{ activityId, withSelectQuestion }} payload.activityId
         * @returns {Promise<void>}
         */
        async loadActivityQuestions({ commit, dispatch }, payload) {
            const { activityId, withSelectQuestion } = payload;

            try {
                dispatch('setLoadingQuestions', {
                    activityId,
                    loading: true,
                });
                dispatch('setErrorQuestions', {
                    activityId,
                    error: false,
                });

                const { data: respData } = await getActivityQuestions(activityId);
                const questions = respData.data;

                commit('setActivityQuestions', {
                    activityId,
                    questions,
                });

                if (withSelectQuestion) {
                    commit('longread/changeCurrentQuestionId', {
                        blockId: activityId,
                        questionId: questions[0].id,
                    }, {
                        root: true,
                    });
                }
            } catch (e) {
                console.error(e);
                dispatch('setErrorQuestions', {
                    activityId,
                    error: true,
                });
            } finally {
                dispatch('setLoadingQuestions', {
                    activityId,
                    loading: false,
                });
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId }} payload
         */
        async loadQuestionsWithRoutes({ dispatch, state, getters, commit }, payload) {
            const { activityId } = payload;

            commit('setRoutesLoadingProgress', 0);

            await dispatch('loadActivityQuestions', { activityId });

            const questions = getters.getQuestions(activityId);
            const questionsChunks = chunk(questions, CHUNK_SIZE_QUESTIONS);

            const progressStep = 100 / questions.length;

            commit('setRoutesLoadingProgress', progressStep);

            await questionsChunks.reduce(async (lastChunkLoading, questionsChunk) => {
                await lastChunkLoading;

                const chunkPromises = questionsChunk.map(async q => {
                    await dispatch('loadQuestionRoutes', {
                        activityId,
                        questionId: q.id,
                    });
                    commit('setRoutesLoadingProgress', state.fullRoutesLoadingProgress + progressStep);
                });

                return Promise.all(chunkPromises);
            }, Promise.resolve());
        },

        /**
         * @param ctx
         * @param {{ activityId, questionId }} payload
         * @returns {Promise<void>}
         */
        async copyActivityQuestion({ dispatch, commit }, payload) {
            const { activityId, questionId } = payload;
            const { data: respData } = await copyPageActivityQuestion(activityId, questionId);
            const newQuestion = respData.data;

            commit('insertActivityQuestionAfter', {
                activityId,
                questionId,
                questionData: newQuestion,
            });

            dispatch('loadActivitySections', {
                activityId,
                silent: true,
                cancelToken: createCancelHttp('cancelLoadActivitySections').token,
            });

            return newQuestion;
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId }} payload
         * @returns {Promise<void>}
         */
        async removeActivityQuestion({ commit }, payload) {
            const { activityId, questionId } = payload;

            await removePageActivityQuestion(activityId, questionId);

            commit('removeActivityQuestion', payload);
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, sectionsIds, questionIds }} payload
         * @returns {Promise<void>}
         */
        async removeActivitySectionsOrQuestions({ commit, dispatch, getters }, payload) {
            const { activityId, sectionsIds, questionIds } = payload;

            await bulkRemoveSectionOrQuestions(activityId, {
                sections: sectionsIds,
                questions: questionIds,
            });

            if (questionIds && questionIds.length) {
                const activityQuestions = getters.getQuestions(activityId);
                const activityQuestionById = keyBy(activityQuestions, 'id');
                const newActivityQuestionById = omit(activityQuestionById, questionIds);
                const newActivityQuestions = Object.values(newActivityQuestionById);

                commit('setActivityQuestions', {
                    activityId,
                    questions: newActivityQuestions,
                });
            }

            if (sectionsIds && sectionsIds.length) {
                const activitySections = getters.getActivitySections(activityId);
                const activitySectionsById = keyBy(activitySections, 'id');
                const newActivitySectionsById = omit(activitySectionsById, questionIds);
                const newActivitySections = Object.values(newActivitySectionsById);

                dispatch('setSectionsList', {
                    activityId,
                    data: newActivitySections,
                });
            }

            dispatch('recalcAllSection', { activityId });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId }} payload
         * @returns {Promise<void>}
         */
        async restoreActivityQuestion({ commit }, payload) {
            const { activityId, questionId } = payload;
            const { data: respData } = await restorePageActivityQuestion(activityId, questionId);

            commit('insertRestoredQuestion', {
                activityId,
                questionData: respData.data,
            });

            return respData.data;
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, sectionId, questions }} payload
         * @returns {Promise<void>}
         */
        setCurrentActivityOrSectionQuestions: ({ commit, getters }, payload) => {
            const { activityId, sectionId: questionSectionId, questions } = payload;

            const sectionId = questionSectionId || getters.getCurrentSection(activityId)?.id;

            if (sectionId) {
                const questionsBySections = getters.getQuestionsGroupedBySections(activityId);

                const questionsBySectionsWithReplacedQuestions = {
                    ...questionsBySections,
                    [sectionId]: questions,
                };

                const newQuestions = Object.values(questionsBySectionsWithReplacedQuestions).flat();

                commit('setActivityQuestions', {
                    activityId,
                    questions: newQuestions,
                });
            } else {
                commit('setActivityQuestions', {
                    activityId,
                    questions,
                });
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, sectionId, order }} payload
         * @returns {Promise<void>}
         */
        async sortActivityQuestions({ commit, getters }, payload) {
            const { activityId, sectionId, order } = payload;
            const prevActivityQuestions = getters.getQuestions(activityId);

            try {
                const activityQuestionsById = keyBy(prevActivityQuestions, 'id');
                const orderedQuestions = at(activityQuestionsById, order).map((item, index) => {
                    if (sectionId && item.test_section_id === sectionId) {
                        return {
                            ...item,
                            test_section_order: index + 1,
                        };
                    }

                    return {
                        ...item,
                        order: index + 1,
                    };
                });
                const orderedQuestionsById = keyBy(orderedQuestions, 'id');

                const newActivityQuestionsById = {
                    ...activityQuestionsById,
                    ...orderedQuestionsById,
                };
                const newActivityQuestions = Object.values(newActivityQuestionsById);

                commit('setActivityQuestions', {
                    activityId,
                    questions: newActivityQuestions,
                });

                await sortPageActivityQuestion(activityId, order);
            } catch (e) {
                console.error(e);
                commit('setActivityQuestions', {
                    activityId,
                    questions: prevActivityQuestions,
                });
                throw e;
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, withAnswers, cancelToken }} payload
         * @returns {Promise<void>}
         */
        async saveActivityQuestion({ commit, getters }, payload) {
            try {

                const { activityId, questionId, cancelToken, withAnswers } = payload;
                const questionWithAnswers = getters.getQuestion(activityId, questionId);

                const questionData = withAnswers ? questionWithAnswers : omit(questionWithAnswers, ['answers']);

                const { data } = await changePageActivityQuestion(activityId, questionId, questionData, { cancelToken });

                commit('replaceActivityQuestion', {
                    activityId,
                    questionId,
                    questionData: withAnswers ? data.data : omit(data.data, ['answers']),
                });
            } catch (e) {
                if (isHttpCancel(e)) return;

                console.error('[changeActivityQuestion]', e);

                createSnackbar({
                    type: 'error',
                    message: 'Ошибка сохранения вопроса',
                });

                throw e;
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, switchData }} payload
         * @returns {Promise<void>}
         */
        async switchAnswerWithChildCorrectAnswer({ dispatch }, payload) {
            const { activityId, questionId, switchData } = payload;

            if (switchData.new?.id) {
                await changePageActivityQuestionAnswer(
                    questionId,
                    switchData.new?.id,
                    {
                        ...switchData.new,
                        is_correct: 1,
                    },
                );
            }

            if (switchData.current?.id) {
                await changePageActivityQuestionAnswer(
                    questionId,
                    switchData.current?.id,
                    {
                        ...switchData.current,
                        is_correct: 0,
                    },
                );
            }

            if (switchData.new?.id) {
                dispatch('changeActivityQuestionAnswer', {
                    activityId,
                    questionId,
                    answerData: {
                        ...switchData.new,
                        parent_id: switchData.parent?.id,
                        is_correct: 1,
                    },
                });
            }

            if (switchData.current?.id) {
                dispatch('changeActivityQuestionAnswer', {
                    activityId,
                    questionId,
                    answerData: {
                        ...switchData.current,
                        parent_id: switchData.parent?.id,
                        is_correct: 0,
                    },
                });
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, answerData }} payload
         */
        changeActivityQuestionAnswer({ commit, getters }, { activityId, questionId, answerData }) {
            const oldQuestion = getters.getQuestion(activityId, questionId);
            const newQuestion = {
                ...oldQuestion,
                answers: oldQuestion.answers.map(answer => {
                    if (answerData.parent_id && answer.id === answerData.parent_id) {
                        return {
                            ...answer,
                            children: answer.children.map(child => {
                                if (child.id === answerData.id) {
                                    return {
                                        ...child,
                                        ...answerData,
                                    };
                                }

                                return child;
                            }),
                        };
                    }

                    if (answer.id === answerData.id) {
                        return {
                            ...answer,
                            ...answerData,
                        };
                    }

                    return answer;
                }),
            };

            commit('replaceActivityQuestion', {
                activityId,
                questionId,
                questionData: newQuestion,
            });
        },

        /**
         * @param {import('vuex').ActionContext}
         * @param {{ activityId, questionId, answerData }} payload
         */
        removeActivityQuestionAnswer({ getters, commit }, payload) {
            const { activityId, questionId, answerData } = payload;
            const oldQuestion = getters.getQuestion(activityId, questionId);

            const newAnswers = answerData.parent?.id
                ? oldQuestion.answers.map(answer => {
                    if (answer.children && answer.id === answerData.parent.id) {
                        return {
                            ...answerData.parent,
                            children: answer.children.filter(child => child.id !== answerData.id),
                        };
                    }

                    return answer;
                })
                : oldQuestion.answers.filter(answer => answer.id !== answerData.id);

            const newQuestion = {
                ...oldQuestion,
                answers: newAnswers,
            };

            commit('replaceActivityQuestion', {
                activityId,
                questionId,
                questionData: newQuestion,
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, order, parentId }} payload
         * @returns {Promise<void>}
         */
        async sortActivityQuestionAnswers({ getters, commit }, payload) {
            const { activityId, questionId, parentId, order } = payload;

            const getOrderedAnswers = answers => {
                const answersByIds = keyBy(answers, 'id');

                return order
                    .map(id => answersByIds[id])
                    .filter(Boolean)
                    .map((child, ind) => ({
                        ...child,
                        order: ind,
                    }));
            };

            const oldQuestions = getters.getActivityQuestionsData(activityId).questions;
            const newQuestions = oldQuestions.map(item => {
                if (item.id === questionId) {
                    if (parentId) {
                        const parentAnswer = item.answers.find(answerItem => answerItem.id === parentId);

                        if (!parentAnswer) return item;

                        return {
                            ...item,
                            answers: item.answers.map(answerItem => {
                                if (answerItem.id !== parentId) return answerItem;

                                return {
                                    ...answerItem,
                                    children: getOrderedAnswers(answerItem.children),
                                };
                            }),
                        };
                    }

                    return {
                        ...item,
                        answers: getOrderedAnswers(item.answers),
                    };
                }

                return item;
            });

            commit('setActivityQuestions', {
                activityId,
                questions: newQuestions,
            });

            await sortPageActivityQuestionAnswer(questionId, order);
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId }} payload
         * @returns {Promise<void>}
         */
        async loadActivitySections({ dispatch, getters }, payload) {
            const { activityId, silent, cancelToken } = payload;

            try {
                if (!silent) {
                    dispatch('setLoadingSections', {
                        activityId,
                        loading: true,
                    });
                }

                dispatch('setErrorSections', {
                    activityId,
                    error: false,
                });

                const { data: respData } = await getTestSections(activityId, { cancelToken });

                dispatch('setSectionsList', {
                    activityId,
                    data: respData.data,
                });


                if (!silent) {
                    const sections = getters.getActivitySections(activityId);

                    dispatch('selectActivitySection', {
                        activityId,
                        sectionId: sections[0].id,
                    });
                }
            } catch (e) {
                if (isHttpCancel(e)) return;

                dispatch('setErrorSections', {
                    activityId,
                    error: true,
                });
            } finally {
                dispatch('setLoadingSections', {
                    activityId,
                    loading: false,
                });
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, section }} payload
         * @returns {Promise<void>}
         */
        updateActivitySection: ({ commit, getters, dispatch }, payload) => {
            try {
                const { activityId, section } = payload;
                const blockSections = getters.getActivitySections(activityId);

                commit('changeData', {
                    type: 'sections',
                    activityId,
                    data: {
                        sections: replaceItemInArrayById(section, blockSections),
                    },
                });

                dispatch('sendUpdateSectionDebounced', {
                    sectionId: section.id,
                    data: section,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ sectionId, data }} payload
         * @returns {Promise<void>}
         */
        sendUpdateSectionDebounced: async ({ dispatch }, payload) => {
            const { sectionId } = payload;
            const cancelToken = createCancelHttp(`sendUpdateSection:${sectionId}`).token;

            await delay(300);

            await dispatch('sendUpdateSection', {
                ...payload,
                config: { cancelToken },
            });
        },

        /**
         * @param injectee
         * @param {{ sectionId, data, config?: import('axios').AxiosRequestConfig }} payload
         * @returns {Promise<void>}
         */
        sendUpdateSection: async (injectee, payload) => {
            try {
                const { sectionId, data, config } = payload;

                await updateTestSection({
                    sectionId,
                    data,
                    config,
                });
            } catch (e) {
                if (isHttpCancel(e)) return;

                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, sectionId }} payload
         */
        selectActivitySection: ({ commit, getters }, payload) => {
            const { activityId } = payload;

            const sectionQuestions = getters.getSectionQuestions(payload.activityId, payload.sectionId);

            commit('setSelectedSection', payload);

            commit('longread/changeCurrentQuestionId', {
                blockId: activityId,
                questionId: sectionQuestions[0]?.id ?? null,
            }, {
                root: true,
            });
        },

        /**
         * @param injectee
         * @param {{ activityId }} payload
         * @returns {Promise<void>}
         */
        createSection: async ({ getters, dispatch }, payload) => {
            try {
                const { activityId } = payload;
                const blockSections = getters.getActivitySections(activityId);
                const maxOrder = Math.max(0, ...blockSections.map(s => s.order));
                const newSectionData = getSection({
                    order: maxOrder + 1,
                });
                const { data: respData } = await createTestSection(activityId, newSectionData);
                const newSection = respData.data;

                await dispatch('longread/addActivityQuestion', {
                    activityId,
                    sectionId: newSection.id,
                }, {
                    root: true,
                });

                dispatch('selectActivitySection', {
                    activityId,
                    sectionId: newSection.id,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, sectionId }} payload
         * @returns {Promise<void>}
         */
        removeSection: async ({ getters, dispatch }, payload) => {
            try {
                const { activityId, sectionId } = payload;

                await deleteTestSection(sectionId);

                const sections = getters.getActivitySections(activityId);

                const removeSectionIndex = sections.findIndex(s => s.id === sectionId);
                const sectionForActive = sections[removeSectionIndex + 1] || sections[removeSectionIndex - 1];
                const newSections = sections.toSpliced(removeSectionIndex, 1);

                dispatch('selectActivitySection', {
                    activityId,
                    sectionId: sectionForActive.id,
                });

                dispatch('setSectionsList', {
                    activityId,
                    data: newSections,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, order }} payload
         */
        setActivitySectionOrder: async ({ dispatch, getters }, payload) => {
            const { activityId, order } = payload;
            const orderedSections = getters.getActivitySections(activityId).map(item => ({
                ...item,
                order: order.indexOf(item.id),
            }));

            await dispatch('setSectionsList', {
                activityId,
                data: orderedSections,
            });
        },

        /**
         * @param injectee
         * @param {{ activityId, order }} payload
         * @returns {Promise<void>}
         */
        sortActivitySections: async ({ dispatch, getters }, payload) => {
            const { activityId, order } = payload;
            const prevOrder = getters.getActivitySections(activityId).map(s => s.id);

            try {
                await dispatch('setActivitySectionOrder', {
                    activityId,
                    order,
                });
                await sortTestSections({
                    activityId,
                    order,
                }, {
                    cancelToken: createCancelHttp('sortTestSections').token,
                });
            } catch (e) {
                if (isHttpCancel(e)) return;

                console.error(e);
                await dispatch('setActivitySectionOrder', {
                    activityId,
                    order: prevOrder,
                });
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, destSectionId, questionsIds, position }} payload
         * @returns {Promise<void>}
         */
        moveQuestionsToSection: async ({ getters, commit, dispatch, rootGetters }, payload) => {
            const { activityId, destSectionId, questionsIds, position } = payload;
            const prevActivityQuestions = getters.getQuestions(activityId);

            try {
                const questionsIdsNumbered = questionsIds.map(Number);
                const activityQuestionsById = keyBy(prevActivityQuestions, 'id');
                const sectionQuestions = getters.getSectionQuestions(Number(activityId), Number(destSectionId));
                const sectionQuestionsIds = map(sectionQuestions, 'id');
                const newSectionQuestionsIds = sectionQuestionsIds.toSpliced(position ?? sectionQuestionsIds.length, 0, ...questionsIdsNumbered);

                const newSectionQuestions = at(activityQuestionsById, newSectionQuestionsIds).map((item, index) => ({
                    ...item,
                    test_section_id: Number(destSectionId),
                    test_section_order: index + 1,
                }));
                const newSectionQuestionsById = keyBy(newSectionQuestions, 'id');
                const newActivityQuestionsById = {
                    ...activityQuestionsById,
                    ...newSectionQuestionsById,
                };
                const newActivityQuestions = Object.values(newActivityQuestionsById);

                commit('setActivityQuestions', {
                    activityId,
                    questions: newActivityQuestions,
                });

                const currentQuestionId = rootGetters['longread/getBlockCurrentQuestion'](activityId);
                const currentSectionQuestionsIds = getters.getCurrentSectionQuestions(activityId);

                if (!currentSectionQuestionsIds.includes(currentQuestionId)) {
                    await dispatch('selectActivitySection', {
                        activityId,
                        sectionId: getters.getCurrentSection(activityId).id,
                    });
                }

                await moveQuestionsToSection(activityId, {
                    questions: questionsIdsNumbered,
                    target_test_section_id: destSectionId,
                });
                await dispatch('sortActivityQuestions', {
                    activityId,
                    sectionId: destSectionId,
                    order: newSectionQuestionsIds,
                });

                dispatch('recalcAllSection', { activityId });
            } catch (e) {
                console.error(e);
                commit('setActivityQuestions', {
                    activityId,
                    questions: prevActivityQuestions,
                });
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, icon }} payload
         * @returns {Promise<void>}
         */
        applyStyleForAllActivitySections: async ({ dispatch, getters }, payload) => {
            const { activityId, icon } = payload;
            const prevActivitySections = getters.getActivitySections(activityId);

            try {
                const newActivitySections = prevActivitySections.map(item => ({
                    ...item,
                    icon: cloneDeep(icon),
                }));

                dispatch('setSectionsList', {
                    activityId,
                    data: newActivitySections,
                });

                await applySectionsStyleIcon(activityId, icon);
            } catch (e) {
                console.error(e);
                dispatch('setSectionsList', {
                    activityId,
                    data: prevActivitySections,
                });
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId, sectionId }} payload
         */
        // eslint-disable-next-line max-lines-per-function
        recalcSectionOptions: async ({ dispatch, getters, rootGetters }, payload) => {
            try {
                const { activityId, sectionId } = payload;
                const section = getters.getSection(activityId, sectionId);
                const block = rootGetters['longread/getActivity'](activityId);

                if (!section) return;

                const maxShouldAnswered = getters.getSectionQuestionsShouldAnsweredCount({
                    activityId,
                    sectionId,
                });

                if (!isNil(section.display_questions_count)) {
                    const isMoreThanMaxShouldAnswered = Number(section.display_questions_count) >= maxShouldAnswered;
                    const hasInformationQuestion = getters.getIsHasInformationQuestion({
                        activityId,
                        sectionId,
                    });
                    const hasUnscoredQuestion = getters.getIsHasUnscoredQuestion({
                        activityId,
                        sectionId,
                    });

                    if (
                        isMoreThanMaxShouldAnswered ||
                        hasInformationQuestion ||
                        hasUnscoredQuestion ||
                        block.data.settings?.score?.type === SCORE_TYPE.EACH_QUESTION
                    ) {
                        await dispatch('updateActivitySection', {
                            activityId,
                            section: {
                                ...section,
                                display_questions_count: null,
                            },
                        });
                    }
                }
            } catch (e) {
                console.error('[recalcSectionOptions]', e);
                throw e;
            }
        },

        /**
         * @param injectee
         * @param {{ activityId }} payload
         */
        recalcAllSection: async ({ getters, dispatch }, payload) => {
            const { activityId } = payload;
            const sections = getters.getActivitySections(activityId);

            await Promise.all(sections.map(sec => dispatch('recalcSectionOptions', {
                activityId,
                sectionId: sec.id,
            })));
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId }} payload
         * @returns {Promise<void>}
         */
        async loadActivityQuestionRoutes({ commit }, payload) {
            const { activityId, questionId } = payload;

            try {
                const { data } = await getActivityQuestionRoutes({
                    activityId,
                    questionId,
                });

                commit('setSelectedQuestionRoutes', data.data);
                commit('setQuestionRoutes', {
                    questionId,
                    routes: data.data,
                });
            } catch (error) {
                console.error(error);
            }
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, questionRoutes }} payload
         * @returns {Promise<void>}
         */
        async sortQuestionRoutes({ commit }, payload) {
            const { activityId, questionId, routes } = payload;

            try {
                const orderedRoutesIds = routes.toSorted((a, b) => a.order - b.order).map(q => q.id);

                commit('setSelectedQuestionRoutes', routes);

                await sortActivityQuestionRoutes(
                    {
                        activityId,
                        questionId,
                        routes: orderedRoutesIds,
                    },
                    {
                        cancelToken: createCancelHttp('sortActivityQuestionRoutes').token,
                    },
                );
            } catch (error) {
                if (isHttpCancel(error)) return;

                console.error(error);
            }
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, order }} payload
         * @returns {Promise<void>}
         */
        async createQuestionRoute({ commit, getters }, payload) {
            try {
                const { activityId, questionId, data } = payload;

                commit('setIsCreatingRoute', true);

                const route = {
                    name: `Маршрут №${data.order ?? 1}`,
                    destination_type: QUESTION_ROUTE_DESTINATION_TYPES.NEXT,
                    destination_id: null,
                    ...data,
                };

                const { data: resp } = await createActivityQuestionRoute({
                    activityId,
                    questionId,
                    route,
                });

                commit(
                    'setSelectedQuestionRoutes',
                    [...getters.getSelectedQuestionRoutes, resp.data],
                );

                commit('setQuestionRoutes', {
                    questionId,
                    routes: [...(getters.getQuestionRoutesData(questionId)?.routes ?? []), resp.data],
                });

                return resp.data;
            } catch (error) {
                console.error(error);
                throw error;
            } finally {
                commit('setIsCreatingRoute', false);
            }
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, route }} payload
         * @returns {Promise<void>}
         */
        async updateActivityQuestionRoute({ commit, getters }, payload) {
            const { questionId, route } = payload;

            try {
                const { data } = await updateActivityQuestionRoute(payload);

                commit(
                    'setSelectedQuestionRoutes',
                    getters.getSelectedQuestionRoutes.map(routeItem => (routeItem.id === route.id ? data.data : routeItem)),
                );
                commit('changeQuestionRoute', {
                    questionId,
                    route: data.data,
                });
            } catch (error) {
                console.error(error);
            }
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, routeId }} payload
         * @returns {Promise<void>}
         */
        async removeQuestionRoute({ commit, getters }, payload) {
            try {
                await removeActivityQuestionRoute(payload);

                const questionRoutes = getters.getSelectedQuestionRoutes;

                commit('setSelectedQuestionRoutes', questionRoutes.filter(route => String(route.id) !== String(payload.routeId)));

                commit('setQuestionRoutes', {
                    questionId: payload.questionId,
                    routes: (getters.getQuestionRoutesData(payload.questionId)?.routes ?? [])
                        .filter(route => String(route.id) !== String(payload.routeId)),
                });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        /**
         * @param {import('vuex').ActionContext} ctx
         */
        resetSelectedQuestionRoutes({ commit }) {
            commit('setSelectedQuestionRoutes', []);
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId }} payload
         */
        loadQuestionRoutes: async ({ commit }, payload) => {
            const { activityId, questionId } = payload;

            commit('setQuestionRoutesLoading', {
                questionId,
                loading: true,
            });

            try {
                const { data } = await getQuestionRoutes(activityId, questionId);
                const routes = data.data;

                commit('setQuestionRoutes', {
                    questionId,
                    routes,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }

            commit('setQuestionRoutesLoading', {
                questionId,
                loading: false,
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, routeId, data }} payload
         */
        updateQuestionRoute: async ({ commit, getters }, payload) => {
            const { activityId, questionId, routeId, data } = payload;

            try {
                const route = getters.getQuestionRoute(questionId, routeId) ?? {};
                const { conditions, ...routeData } = route;
                const resp = await updateQuestionRoute(activityId, questionId, routeId, {
                    ...routeData,
                    ...data,
                });

                commit('changeQuestionRoute', {
                    questionId,
                    route: resp.data.data,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, routeId, conditionId, data }} payload
         */
        updateRouteCondition: async ({ commit, getters }, payload) => {
            const { activityId, questionId, routeId, conditionId, data } = payload;

            try {
                const resp = await updateRouteCondition(activityId, questionId, routeId, conditionId, {
                    ...getters.getQuestionRouteCondition(questionId, routeId, conditionId),
                    ...data,
                });

                commit('changeRouteConditions', {
                    questionId,
                    routeId,
                    conditions: [resp.data.data],
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, routeId, condition }} payload
         */
        createRouteCondition: async ({ commit }, payload) => {
            try {
                commit('setIsCreatingCondition', true);

                const { activityId, questionId, routeId, condition } = payload;
                const { id, ...data } = condition;
                const resp = await createRouteCondition(activityId, questionId, routeId, data);

                commit('clearNewConditionData');

                commit('addRouteCondition', {
                    questionId,
                    routeId,
                    condition: resp.data.data,
                });

            } catch (e) {
                console.error(e);
                throw e;
            } finally {
                commit('setIsCreatingCondition', false);
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ questionId, routeId, }} payload
         */
        addNewRouteCondition({ commit, getters }, payload) {
            const { activityId, routeId, questionId } = payload;

            const questionType = getters.getQuestion(activityId, questionId)?.type;

            const conditions = getters.getQuestionRoutesData(questionId)
                ?.routes
                ?.find(route => String(route.id) === String(routeId))
                ?.conditions ?? [];

            const lastCondition = conditions.at(-1);

            const operator = QUESTION_TYPES_WITH_DISABLED_CONDITION_AND.includes(questionType)
                ? QUESTION_ROUTE_CONDITIONS_TYPES.OR
                : QUESTION_ROUTE_CONDITIONS_TYPES.AND;

            commit('setNewConditionData', getNewQuestionRouteConditions({
                order: conditions.length,
                operator: lastCondition.operator || operator,
            }));
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId, routeId, conditionId }} payload
         */
        deleteRouteCondition: async ({ commit }, payload) => {
            const { activityId, questionId, routeId, conditionId } = payload;

            try {
                if (conditionId === -1) {
                    commit('clearNewConditionData');

                    return;
                }

                await deleteRouteCondition(activityId, questionId, routeId, conditionId);

                commit('deleteRouteCondition', {
                    questionId,
                    routeId,
                    conditionId,
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionId }} payload
         */
        selectFlowDashboardQuestion({ dispatch, commit, rootGetters }, payload) {
            const { activityId, questionId } = payload;

            if (activityId && questionId) {
                const selectedRoutes = rootGetters['questions/getQuestionRoutesData'](questionId)?.routes ?? [];

                commit('questions/setSelectedQuestionRoutes', selectedRoutes, {
                    root: true,
                });
                dispatch('longread/activateBlock', {
                    blockId: activityId,
                    element: questionId,
                }, { root: true });
            }
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionIds }} payload
         */
        applyUnreachableQuestions({ commit, getters }, payload) {
            const { activityId, questionIds } = payload;
            const questions = getters.getQuestions(activityId);

            const newQuestions = questions.map(q => ({
                ...q,
                is_unreachable: questionIds.includes(q.id),
            }));

            commit('setActivityQuestions', {
                activityId,
                questions: newQuestions,
            });
        },

        /**
         * @param {import('vuex').ActionContext} ctx
         * @param {{ activityId, questionIds }} payload
         */
        applyAffectsResultQuestions({ commit, getters }, payload) {
            const { activityId, questionIds } = payload;
            const questions = getters.getQuestions(activityId);

            const newQuestions = questions.map(q => ({
                ...q,
                affects_result: questionIds.includes(q.id),
            }));

            commit('setActivityQuestions', {
                activityId,
                questions: newQuestions,
            });
        },
    },
};

export default module;
